import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | undefined;
  // Second socket instance dedicated to the User server (port 8887)
  private userSocket: Socket | undefined;

  constructor(private readonly authService: AuthService) {
    window.addEventListener('vlug:auth-logout', () => this.disconnectAll());
  }

  /**
   * Key-order-independent comparison of two handshake query objects.
   *
   * Values are compared as strings because that is what actually goes on the
   * wire — `api: false` and `api: 'false'` produce an identical handshake and
   * must not count as a change, or every reconnect would trigger another one.
   */
  private static sameQuery(a: unknown, b: Record<string, unknown>): boolean {
    const left = (a ?? {}) as Record<string, unknown>;
    const leftKeys = Object.keys(left).sort();
    const rightKeys = Object.keys(b).sort();
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every(
      (key, i) => rightKeys[i] === key && String(left[key]) === String(b[key]),
    );
  }

  connect(options: any = {}, serverType: 'pos' | 'user' = 'pos'): void {
    const query = options.query || {};
    const user = this.authService.getUser();
    const token = options.token || user?._adminToken || user?._token;
    if (!token) return;
    const socketUrl = serverType === 'user'
      ? (environment.socketUserUrl || 'http://localhost:8887')
      : (environment.socketPosUrl || 'http://localhost:8888');

    const target = serverType === 'user' ? 'userSocket' : 'socket';

    if (!this[target]) {
      this[target] = io(socketUrl, {
        transports: ['websocket'],
        autoConnect: true,
        query,
        auth: { token }
      });
    } else {
      const previousToken = (this[target]!.auth as { token?: string } | undefined)?.token;
      this[target]!.auth = { token };

      // The handshake query is only transmitted when a connection is opened, so
      // assigning io.opts.query on a live socket changes nothing by itself. The
      // reconnect below used to be gated on the token alone — and the token does
      // NOT change when a merchant switches store. Result: the order list
      // fetched store B over HTTP while the socket stayed in store A's room, so
      // live orders for the store on screen never arrived and stale ones for the
      // previous store did. Comparing the query too is what makes a store switch
      // actually re-handshake.
      const queryChanged =
        Object.keys(query).length > 0 &&
        !SocketService.sameQuery(this[target]!.io.opts.query, query);

      if (queryChanged) {
        this[target]!.io.opts.query = query;
      }

      if (this[target]!.connected && (previousToken !== token || queryChanged)) {
        this[target]!.disconnect().connect();
      } else if (!this[target]!.connected) {
        this[target]!.connect();
      }
    }
  }

  disconnect(serverType: 'pos' | 'user' = 'pos'): void {
    const targetName = serverType === 'user' ? 'userSocket' : 'socket';
    this[targetName]?.removeAllListeners();
    this[targetName]?.disconnect();
    this[targetName] = undefined;
  }

  disconnectAll(): void {
    this.disconnect('pos');
    this.disconnect('user');
  }

  emit(eventName: string, data: any, serverType: 'pos' | 'user' = 'pos'): void {
    const target = serverType === 'user' ? this.userSocket : this.socket;
    if (target) target.emit(eventName, data);
  }

  on<T>(eventName: string, serverType: 'pos' | 'user' = 'pos'): Observable<T> {
    return new Observable<T>(observer => {
      if (serverType === 'user') {
        if (!this.userSocket) this.connect({}, 'user');
        if (!this.userSocket) return;
        const handler = (data: T) => observer.next(data);
        this.userSocket.on(eventName, handler);
        return () => { if (this.userSocket) this.userSocket.off(eventName, handler); };
      } else {
        if (!this.socket) this.connect();
        if (!this.socket) return;
        const handler = (data: T) => observer.next(data);
        this.socket.on(eventName, handler);
        return () => { if (this.socket) this.socket.off(eventName, handler); };
      }
    });
  }

  onConnect(serverType: 'pos' | 'user' = 'pos'): Observable<void> {
    return new Observable<void>(observer => {
      const target = serverType === 'user' ? this.userSocket : this.socket;
      if (!target) return;
      const handler = () => observer.next();
      target.on('connect', handler);
      return () => target.off('connect', handler);
    });
  }
}